import { useEffect, useRef } from 'react';
import { Button } from '../../components/ui';
import { AuthCard } from './AuthCard';
import { CodeForm } from './CodeForm';
import { RecoveryCodes } from './RecoveryCodes';
import { useMfaConfirm, useMfaSetup, useOpenApp } from './queries';

const KEY_GROUP = /.{1,4}/g;

/** `JBSWY3DPEHPK3PXP` → `JBSW Y3DP EHPK 3PXP`, easier to type off the screen. */
const groupedKey = (secret: string) => secret.match(KEY_GROUP)?.join(' ') ?? secret;

/** The first login online (W20): the QR code for the authenticator app, then its first code. */
export function MfaSetupScreen({ email }: { readonly email: string }) {
  const openApp = useOpenApp();
  const setup = useMfaSetup();
  const confirm = useMfaConfirm();
  const isStarted = useRef(false);

  useEffect(() => {
    if (isStarted.current) return;
    isStarted.current = true;
    setup.mutate();
  }, [setup]);

  if (confirm.data) {
    return (
      <AuthCard title="Резервные коды" email={email}>
        <RecoveryCodes codes={confirm.data.recoveryCodes} onDone={openApp} />
      </AuthCard>
    );
  }
  return (
    <AuthCard title="Подключите приложение-аутентификатор" email={email}>
      <p className="text-fg-2">
        Вход в систему защищён вторым фактором. Установите Google Authenticator (или другое приложение для одноразовых кодов) и отсканируйте QR-код.
      </p>
      {setup.isError && (
        <div className="flex flex-col gap-2">
          <p role="alert" className="text-danger-fg">
            {setup.error.message}
          </p>
          <Button type="button" variant="secondary" onClick={() => setup.mutate()}>
            Повторить
          </Button>
        </div>
      )}
      {setup.data && (
        <div className="flex flex-col items-center gap-2">
          <img src={setup.data.qrDataUrl} alt="QR-код для приложения-аутентификатора" width={180} height={180} />
          <p className="text-center text-[12px] text-fg-3">
            Не сканируется? Введите ключ вручную:
            <br />
            <span className="text-[13px] font-medium tabular-nums tracking-wider text-fg-1">{groupedKey(setup.data.secret)}</span>
          </p>
        </div>
      )}
      <CodeForm
        label="Код из приложения"
        submitLabel="Подключить"
        isAppCode
        isPending={confirm.isPending || !setup.data}
        error={confirm.error}
        onSubmit={(code) => confirm.mutate(code)}
      />
    </AuthCard>
  );
}
